<template>
  <div class="custom-address-input">
    <div class="address-input-container">
      <i class="fas fa-map-marker-alt address-icon" />
      <input
        :ref="inputRef"
        v-model="addressValue"
        type="text"
        class="address-input"
        :placeholder="placeholder"
        autocomplete="off"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown="handleKeydown"
      >
      <button
        v-if="addressValue"
        type="button"
        class="clear-address-btn"
        tabindex="-1"
        @click="clearAddress"
      >
        <i class="fas fa-times" />
      </button>
      <div
        v-if="loading"
        class="address-spinner"
      >
        <i class="fas fa-spinner fa-spin" />
      </div>
    </div>
    <div
      v-if="showDropdown && suggestions.length > 0"
      :ref="dropdownRef"
      class="address-dropdown"
    >
      <div
        v-for="(suggestion, index) in suggestions"
        :key="index"
        class="address-option"
        :class="{ 'active': selectedIndex === index }"
        @mousedown.prevent="selectSuggestion(suggestion)"
        @mouseenter="selectedIndex = index"
      >
        <div class="address-main">
          <i class="fas fa-map-marker-alt text-primary mr-2" />
          <span v-html="highlightMatch(suggestion.main_text, addressValue)" />
        </div>
        <div class="address-secondary">
          {{ suggestion.secondary_text }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AddressAutocomplete',
  props: {
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Digite um endereço...',
    },
    inputRef: {
      type: String,
      default: 'addressInput',
    },
    dropdownRef: {
      type: String,
      default: 'addressDropdown',
    },
    googleApiKey: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      addressValue: this.value,
      suggestions: [],
      showDropdown: false,
      selectedIndex: -1,
      searchTimeout: null,
      placesService: null,
    }
  },
  watch: {
    value(val) {
      this.addressValue = val
    },
  },
  methods: {
    async initGooglePlaces() {
      if (this.placesService) return
      await this.loadGooglePlacesAPI()
      if (window.google?.maps?.places) {
        this.placesService = new window.google.maps.places.AutocompleteService()
      }
    },
    loadGooglePlacesAPI() {
      return new Promise((resolve, reject) => {
        if (window.google?.maps?.places) {
          resolve()
          return
        }
        if (document.querySelector('script[src*="maps.googleapis.com"]')) {
          const checkLoaded = setInterval(() => {
            if (window.google?.maps?.places) {
              clearInterval(checkLoaded)
              resolve()
            }
          }, 100)
          return
        }
        const script = document.createElement('script')
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleApiKey}&libraries=places&callback=initGoogleMaps`
        script.async = true
        script.defer = true
        window.initGoogleMaps = () => {
          delete window.initGoogleMaps
          resolve()
        }
        script.onerror = error => {
          reject(new Error('Falha ao carregar Google Maps API'))
        }
        document.head.appendChild(script)
      })
    },
    handleInput() {
      this.$emit('input', this.addressValue)
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      if (!this.addressValue || this.addressValue.length < 3) {
        this.suggestions = []
        this.showDropdown = false
        return
      }
      this.initGooglePlaces().then(() => {
        this.searchTimeout = setTimeout(() => this.searchAddresses(this.addressValue), 300)
      })
    },
    handleFocus() {
      this.showDropdown = this.suggestions.length > 0
    },
    handleBlur() {
      setTimeout(() => {
        this.showDropdown = false
        this.selectedIndex = -1
      }, 200)
    },
    handleKeydown(event) {
      if (!this.showDropdown || this.suggestions.length === 0) return
      const actions = {
        ArrowDown: () => {
          event.preventDefault()
          this.selectedIndex = Math.min(this.selectedIndex + 1, this.suggestions.length - 1)
        },
        ArrowUp: () => {
          event.preventDefault()
          this.selectedIndex = Math.max(this.selectedIndex - 1, -1)
        },
        Enter: () => {
          event.preventDefault()
          if (this.selectedIndex >= 0) {
            this.selectSuggestion(this.suggestions[this.selectedIndex])
          }
        },
        Escape: () => {
          this.showDropdown = false
          this.selectedIndex = -1
        },
      }
      actions[event.key]?.()
    },
    clearAddress() {
      this.addressValue = ''
      this.$emit('input', '')
      this.suggestions = []
      this.showDropdown = false
    },
    searchAddresses(address) {
      if (!this.placesService || !address || address.length < 3) return
      const request = {
        input: address,
        componentRestrictions: { country: 'br' },
        types: ['address'],
      }
      this.placesService.getPlacePredictions(request, (predictions, status) => {
        const isOk = status === window.google.maps.places.PlacesServiceStatus.OK
        this.suggestions = isOk && predictions
          ? predictions.map(prediction => ({
            place_id: prediction.place_id,
            main_text: prediction.structured_formatting.main_text,
            secondary_text: prediction.structured_formatting.secondary_text,
            description: prediction.description,
          }))
          : []
        this.showDropdown = isOk && predictions
      })
    },
    selectSuggestion(suggestion) {
      this.addressValue = suggestion.description
      this.$emit('input', suggestion.description)
      this.showDropdown = false
      this.selectedIndex = -1
      this.suggestions = []
      this.$emit('select', suggestion)
    },
    highlightMatch(text, search) {
      if (!search) return text
      const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
      return text.replace(regex, '<strong class="text-primary">$1</strong>')
    },
  },
}
</script>

<style scoped>
custom-address-input {
  position: relative;
  width: 100%;
}

.address-input-container {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background-color: #fff;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.address-input-container:focus-within {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.address-icon {
  position: absolute;
  left: 12px;
  color: #6c757d;
  font-size: 16px;
  z-index: 2;
}

.address-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 12px 16px 12px 40px;
  font-size: 14px;
  border-radius: 6px;
  background: transparent;
}

.address-input::placeholder {
  color: #adb5bd;
}

.clear-address-btn {
  position: absolute;
  right: 40px;
  background: none;
  border: none;
  color: #6c757d;
  padding: 8px;
  cursor: pointer;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 2;
}

.clear-address-btn:hover {
  background-color: #f8f9fa;
  color: #dc3545;
}

.address-spinner {
  position: absolute;
  right: 12px;
  color: #007bff;
  z-index: 2;
}

.address-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  max-height: 300px;
  overflow-y: auto;
  margin-top: 4px;
}

.address-option {
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
  transition: background-color 0.2s ease;
}

.address-option:last-child {
  border-bottom: none;
}

.address-option:hover,
.address-option.active {
  background-color: #f8f9fa;
}

.address-main {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #212529;
  margin-bottom: 4px;
}

.address-secondary {
  font-size: 13px;
  color: #6c757d;
  margin-left: 24px;
}
</style>
